if (!customElements.get('grid-slideshow')) {
  customElements.define(
    'grid-slideshow',
    class GridSlideshow extends HTMLElement {
      static AUTOPLAY_INTERVAL = 4000;

      static DEFAULT_OPTIONS = {
        cellAlign: 'left',
        contain: true,
        pageDots: false,
        prevNextButtons: false,
        wrapAround: true,
        imagesLoaded: true,
        adaptiveHeight: false,
        percentPosition: false,
      };

      static TRACK_DEFAULT_OPTIONS = {
        cellAlign: 'left',
        contain: true,
        pageDots: false,
        prevNextButtons: false,
        wrapAround: false,
        imagesLoaded: true,
        adaptiveHeight: true,
        percentPosition: true,
      };

      static MOBILE_MEDIA_QUERY = '(max-width: 749px)';

      get autoplayEnabled() {
        return this.dataset.autoplay === 'true';
      }

      get isMobile() {
        return window.matchMedia(GridSlideshow.MOBILE_MEDIA_QUERY).matches;
      }

      connectedCallback() {
        this.init();
        this.bindBreakpointListener();
      }

      disconnectedCallback() {
        this.unbindBreakpointListener();
        this.destroy();
      }

      bindBreakpointListener() {
        this.mql = window.matchMedia(GridSlideshow.MOBILE_MEDIA_QUERY);
        this.onBreakpointChange = () => this.init();

        if (this.mql.addEventListener) {
          this.mql.addEventListener('change', this.onBreakpointChange);
        } else {
          this.mql.addListener(this.onBreakpointChange);
        }
      }

      unbindBreakpointListener() {
        if (!this.mql || !this.onBreakpointChange) return;

        if (this.mql.removeEventListener) {
          this.mql.removeEventListener('change', this.onBreakpointChange);
        } else {
          this.mql.removeListener(this.onBreakpointChange);
        }
      }

      init() {
        this.querySelectorAll('[data-grid-slideshow-track]').forEach((el) => this.initTrack(el));
        this.querySelectorAll('[data-grid-slideshow-carousel]').forEach((el) => {
          if (!el.closest('[data-grid-slideshow-track]')) {
            this.initCarousel(el);
          }
        });
      }

      destroy() {
        this.querySelectorAll('[data-grid-slideshow-track], [data-grid-slideshow-carousel]').forEach((el) => {
          if (el.flickityInstance) {
            el.flickityInstance.destroy();
            el.flickityInstance = null;
          }
          el.controlHandlers = null;
          el.hoverHandlers = null;
        });
      }

      setupControls(wrapper, el, flkty, prevSelector, nextSelector) {
        const prevButton = wrapper.querySelector(prevSelector);
        const nextButton = wrapper.querySelector(nextSelector);

        if (!prevButton || !nextButton) return;

        if (el.controlHandlers) {
          prevButton.removeEventListener('click', el.controlHandlers.prev);
          nextButton.removeEventListener('click', el.controlHandlers.next);
          flkty.off('select', el.controlHandlers.update);
        }

        function updateControls() {
          const slideCount = flkty.slides.length;
          const disableNav = slideCount <= 1;

          prevButton.disabled = disableNav;
          nextButton.disabled = disableNav;
          prevButton.hidden = disableNav;
          nextButton.hidden = disableNav;
        }

        const prevHandler = () => flkty.previous();
        const nextHandler = () => flkty.next();

        prevButton.addEventListener('click', prevHandler);
        nextButton.addEventListener('click', nextHandler);
        flkty.on('select', updateControls);
        updateControls();

        el.controlHandlers = {
          prev: prevHandler,
          next: nextHandler,
          update: updateControls,
        };
      }

      setupHoverCarousel(wrapper, el, flkty) {
        const autoplayEnabled = this.autoplayEnabled;

        if (el.hoverHandlers) {
          wrapper.removeEventListener('mouseenter', el.hoverHandlers.enter);
          wrapper.removeEventListener('mouseleave', el.hoverHandlers.leave);
          wrapper.removeEventListener('focusin', el.hoverHandlers.enter);
          wrapper.removeEventListener('focusout', el.hoverHandlers.leave);
        }

        function activateCarousel() {
          wrapper.classList.add('is-active');
          flkty.select(0, false, true);
          if (autoplayEnabled) {
            flkty.playPlayer();
          }
        }

        function deactivateCarousel() {
          wrapper.classList.remove('is-active');
          flkty.stopPlayer();
          flkty.select(0, false, true);
        }

        const enterHandler = () => activateCarousel();
        const leaveHandler = (event) => {
          if (wrapper.contains(event.relatedTarget)) return;
          deactivateCarousel();
        };

        wrapper.addEventListener('mouseenter', enterHandler);
        wrapper.addEventListener('mouseleave', leaveHandler);
        wrapper.addEventListener('focusin', enterHandler);
        wrapper.addEventListener('focusout', leaveHandler);

        el.hoverHandlers = {
          enter: enterHandler,
          leave: leaveHandler,
        };
      }

      initCarousel(el) {
        if (!el || typeof Flickity === 'undefined') return;

        if (el.flickityInstance) {
          el.flickityInstance.destroy();
          el.flickityInstance = null;
        }

        const autoplayEnabled = this.autoplayEnabled;
        const isHoverCarousel = el.dataset.hoverCarousel === 'true';

        let options = { ...GridSlideshow.DEFAULT_OPTIONS };
        if (el.dataset.flickityOptions) {
          try {
            options = { ...options, ...JSON.parse(el.dataset.flickityOptions) };
          } catch (error) {
            console.warn('Grid slideshow: invalid Flickity options', error);
          }
        }

        if (autoplayEnabled) {
          options.autoPlay = GridSlideshow.AUTOPLAY_INTERVAL;
        }

        const flkty = new Flickity(el, options);
        el.flickityInstance = flkty;

        if (isHoverCarousel && autoplayEnabled) {
          flkty.stopPlayer();
        }

        const wrapper = el.closest('.grid-slideshow__carousel-wrapper');
        if (wrapper) {
          this.setupControls(wrapper, el, flkty, '.grid-slideshow__nav--prev', '.grid-slideshow__nav--next');
          if (isHoverCarousel) {
            this.setupHoverCarousel(wrapper, el, flkty);
          }
        }
      }

      initTrack(el) {
        if (!el || typeof Flickity === 'undefined') return;

        if (el.flickityInstance) {
          el.flickityInstance.destroy();
          el.flickityInstance = null;
        }

        // Init nested carousels first so track adaptiveHeight measures collapsed slide height
        el.querySelectorAll('[data-grid-slideshow-carousel]').forEach((carousel) => this.initCarousel(carousel));

        const autoplayEnabled = this.autoplayEnabled;
        const blockCount = parseInt(el.dataset.blockCount, 10) || 0;
        const itemsPerRow = parseInt(el.dataset.itemsPerRow, 10) || 1;
        const needsNav = blockCount > itemsPerRow;

        let options = { ...GridSlideshow.TRACK_DEFAULT_OPTIONS };
        if (el.dataset.flickityOptions) {
          try {
            options = { ...options, ...JSON.parse(el.dataset.flickityOptions) };
          } catch (error) {
            console.warn('Grid slideshow: invalid track Flickity options', error);
          }
        }

        // On mobile, advance one cell at a time regardless of items_per_row grouping
        if (this.isMobile) {
          options.groupCells = false;
        }

        if (autoplayEnabled && needsNav) {
          options.autoPlay = GridSlideshow.AUTOPLAY_INTERVAL;
        }

        const flkty = new Flickity(el, options);
        el.flickityInstance = flkty;

        const wrapper = el.closest('.grid-slideshow__track-wrapper');
        if (wrapper && needsNav) {
          this.setupControls(
            wrapper,
            el,
            flkty,
            '.grid-slideshow__track-nav--prev',
            '.grid-slideshow__track-nav--next'
          );
        }
      }
    }
  );
}
